import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, TrendingUp, Briefcase, ArrowRight, Activity, Gauge } from "lucide-react";
import { BusinessShell, useBiz } from "@/components/app/BusinessShell";
import { StatCard, Spinner, StatusBadge } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { inr } from "@/lib/constants";

function OverviewInner() {
  const { orgId } = useBiz();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { if (orgId && orgId !== "none") api.get(`/organizations/${orgId}/overview`).then(({ data }) => setD(data)); }, [orgId]);
  if (!d) return <Spinner label="Loading command center..." />;
  const k = d.kpis;
  const health = d.business_health;
  const sevColor = { high: "text-red-600 bg-red-50 border-red-200", medium: "text-amber-600 bg-amber-50 border-amber-200", low: "text-slate-600 bg-slate-50 border-slate-200" };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold text-sp-navy">Good day{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1><p className="text-sm text-slate-500">Here's what's happening across your business.</p></div>
        <button onClick={() => nav("/business/projects")} className="text-sm bg-sp-orange hover:bg-sp-orangedark text-white rounded-full px-5 py-2">New Project</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ov-active" label="Active Projects" value={k.active_projects} accent />
        <StatCard label="Project Value" value={inr(k.project_value)} />
        <StatCard label="Pipeline" value={inr(k.pipeline_value)} sub={`${k.leads} leads`} />
        <StatCard label="Outstanding" value={inr(k.outstanding)} sub="Receivables" />
        <StatCard label="Revenue" value={inr(k.revenue)} />
        <StatCard label="Procurement" value={inr(k.procurement_value)} />
        <StatCard label="Delayed" value={k.delayed} sub="At-risk projects" />
        <StatCard label="Quality Issues" value={k.quality_issues} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Business Health */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4"><Gauge className="h-4 w-4 text-sp-orange" /><span className="font-semibold text-sp-navy">Business Health</span></div>
          <div className="flex items-center gap-4">
            <div className="relative h-24 w-24 shrink-0">
              <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
                <path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke="#F2F2F2" strokeWidth="3.5" />
                <path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke="#FF5A00" strokeWidth="3.5"
                  strokeDasharray={`${health.score}, 100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl font-bold text-sp-navy">{health.score}</span></div>
            </div>
            <div className="flex-1 space-y-1.5" data-testid="health-components">
              {Object.entries(health.components).map(([label, val]) => (
                <div key={label} className="flex items-center gap-2 text-xs"><span className="w-20 text-slate-500">{label}</span>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-sp-orange" style={{ width: `${val}%` }} /></div>
                  <span className="w-7 text-right text-slate-600">{val}</span></div>
              ))}
            </div>
          </div>
        </div>

        {/* Needs attention (alerts) */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-4 w-4 text-sp-orange" /><span className="font-semibold text-sp-navy">Needs Attention</span><span className="text-xs text-slate-400">({d.alerts.length})</span></div>
          {d.alerts.length === 0 ? <p className="text-sm text-slate-400 py-4">All clear — nothing needs your attention right now.</p> :
            <div className="space-y-2 max-h-64 overflow-y-auto sp-scroll" data-testid="alerts-list">
              {d.alerts.map((a, i) => (
                <button key={i} onClick={() => nav(a.link)} data-testid={`alert-${i}`} className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-center justify-between ${sevColor[a.severity]}`}>
                  <div><div className="text-sm font-medium">{a.title}</div><div className="text-xs opacity-80">{a.detail}</div></div>
                  <ArrowRight className="h-4 w-4 opacity-60" /></button>
              ))}
            </div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-4"><span className="font-semibold text-sp-navy flex items-center gap-2"><Briefcase className="h-4 w-4 text-sp-orange" />Projects</span>
            <button onClick={() => nav("/business/projects")} className="text-xs text-sp-orange">View all</button></div>
          {d.projects.length === 0 ? <p className="text-sm text-slate-400 py-4">No projects yet.</p> :
            <div className="divide-y">{d.projects.slice(0, 6).map((p) => (
              <button key={p.id} onClick={() => nav(`/app/project/${p.id}`)} data-testid={`ov-project-${p.id}`} className="w-full text-left py-3 flex items-center justify-between hover:bg-slate-50 -mx-2 px-2 rounded">
                <div><div className="text-sm font-medium text-sp-navy">{p.name}</div><div className="text-xs text-slate-400">{p.location} · {inr(p.budget)}</div></div>
                <div className="flex items-center gap-3"><div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block"><div className="h-full bg-sp-orange" style={{ width: `${p.progress}%` }} /></div><span className="text-xs text-slate-500 w-9">{p.progress}%</span><StatusBadge status={p.status} /></div>
              </button>))}</div>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <span className="font-semibold text-sp-navy flex items-center gap-2 mb-4"><Activity className="h-4 w-4 text-sp-orange" />Recent Activity</span>
          {d.recent_activity.length === 0 ? <p className="text-sm text-slate-400">No recent activity.</p> :
            <div className="space-y-3">{d.recent_activity.map((a) => (
              <div key={a.id} className="text-sm"><span className="text-slate-400">{a.user_name}</span> <span className="text-sp-navy">{a.action}</span></div>))}</div>}
        </div>
      </div>
    </div>
  );
}

export default function BusinessOverview() {
  return <BusinessShell title="Command Center"><OverviewInner /></BusinessShell>;
}
